import { type ColumnState, type GridApi, type GridState } from "ag-grid-community"

import {
  isAgGridInternalColumn,
  isExtradataColumn,
  liveColIds,
  persistedAggFuncs,
  persistedColumnOrder,
  savedWidthColumnState,
  takeNewColumns,
  type SavedStateMigration,
} from "~/hooks/useGridState"
import {
  CALENDAR_BLOCK_SENTINEL,
  isCalendarColumn,
  isCalendarOrderEntry,
  isStaleCalendarColumnId,
} from "~/utils/productTableUtils"

/**
 * `liveOrder` with each of `colIds` moved to just after its nearest preceding `referenceOrder` entry
 * that is in the grid, or first when none is. A non-calendar column whose reference neighbour stands
 * for the calendar block (the sentinel, or a calendar id that may since have shifted) goes after
 * the live calendar block. Ids with no live predecessor go after AG Grid's own leading columns. Ids
 * missing from the reference or the grid keep their live position.
 */
export function spliceAfterNearestPredecessor(
  referenceOrder: readonly string[],
  liveOrder: readonly string[],
  colIds: readonly string[],
): string[] {
  const referenceIndex = new Map(referenceOrder.map((colId, i) => [colId, i]))
  const live = new Set(liveOrder)
  const placing = new Set(colIds.filter((colId) => referenceIndex.has(colId) && live.has(colId)))
  const base = liveOrder.filter((colId) => !placing.has(colId))
  const inBase = new Set(base)
  const lastCalendarColumn = base.findLast(isCalendarColumn)

  const anchorOf = (colId: string): string | null => {
    for (let i = referenceIndex.get(colId)! - 1; i >= 0; i--) {
      const previous = referenceOrder[i]!
      if (lastCalendarColumn && !isCalendarColumn(colId) && isCalendarOrderEntry(previous)) {
        return lastCalendarColumn
      }
      if (inBase.has(previous) || placing.has(previous)) return previous
    }
    return null
  }
  const placedAfter = new Map<string | null, string[]>()
  const byReference = [...placing].toSorted(
    (a, b) => referenceIndex.get(a)! - referenceIndex.get(b)!,
  )
  for (const colId of byReference) {
    const anchor = anchorOf(colId)
    const followers = placedAfter.get(anchor)
    if (followers) followers.push(colId)
    else placedAfter.set(anchor, [colId])
  }

  const order: string[] = []
  const emit = (colId: string) => {
    order.push(colId)
    for (const follower of placedAfter.get(colId) ?? []) emit(follower)
  }
  const firstUserColumn = base.findIndex((colId) => !isAgGridInternalColumn(colId))
  const leading = firstUserColumn === -1 ? base.length : firstUserColumn
  for (const colId of base.slice(0, leading)) emit(colId)
  for (const colId of placedAfter.get(null) ?? []) emit(colId)
  for (const colId of base.slice(leading)) emit(colId)
  return order
}

/** A saved order entry from an older save that stood for a calendar column. */
const isStaleCalendarEntry = (colId: string) =>
  isStaleCalendarColumnId(colId) && !isExtradataColumn(colId)

/** Whether a saved-order entry stands for the calendar block, under any id it has gone by. */
const isCalendarBlockEntry = (colId: string) =>
  isCalendarOrderEntry(colId) || isStaleCalendarEntry(colId)

function calendarInsertIndex(
  order: readonly string[],
  calendarColIds: readonly string[],
  index: number,
): number {
  const listed = new Set(order)
  const before = calendarColIds.slice(0, index).findLast((colId) => listed.has(colId))
  if (before !== undefined) return order.indexOf(before) + 1
  const after = calendarColIds.slice(index + 1).find((colId) => listed.has(colId))
  if (after !== undefined) return order.indexOf(after)
  const block = order.indexOf(CALENDAR_BLOCK_SENTINEL)
  return block === -1 ? order.length : block
}

/**
 * `orderedColIds` with the live `calendarColIds` (definition order) in place of the entries that
 * stand for the calendar block: the sentinel, and calendar or stale ids that are no longer live.
 * Live calendar ids the order lists keep their position, so a custom calendar order survives. A live
 * id it lacks goes next to its nearest listed definition-order neighbour, else at the first block
 * entry, else last. Without live calendar ids the order is returned unchanged, so the block
 * position survives until they load.
 */
export function resolveCalendarBlock(
  orderedColIds: readonly string[],
  calendarColIds: readonly string[],
): readonly string[] {
  if (calendarColIds.length === 0) return orderedColIds
  const live = new Set(calendarColIds)
  const listed = new Set(orderedColIds)
  const standsForBlock = (colId: string) => !live.has(colId) && isCalendarBlockEntry(colId)
  if (calendarColIds.every((colId) => listed.has(colId)) && !orderedColIds.some(standsForBlock)) {
    return orderedColIds
  }

  const firstBlockEntry = orderedColIds.findIndex(standsForBlock)
  const order = orderedColIds.flatMap((colId, i) => {
    if (i === firstBlockEntry) return [CALENDAR_BLOCK_SENTINEL]
    return standsForBlock(colId) ? [] : [colId]
  })
  calendarColIds.forEach((colId, i) => {
    if (!listed.has(colId)) order.splice(calendarInsertIndex(order, calendarColIds, i), 0, colId)
  })
  return order.filter((colId) => colId !== CALENDAR_BLOCK_SENTINEL)
}

/** {@link resolveCalendarBlock} on a saved state's column order, against the registered columns. */
export const resolveCalendarBlockInState: SavedStateMigration = (state, columns) => {
  const orderedColIds = state.columnOrder?.orderedColIds
  if (!orderedColIds?.length) return state
  const resolved = resolveCalendarBlock(
    orderedColIds,
    columns.map(({ colId }) => colId).filter(isCalendarColumn),
  )
  if (resolved === orderedColIds) return state
  return { ...state, columnOrder: { ...state.columnOrder, orderedColIds: [...resolved] } }
}

/**
 * Completes a grid state against the registered columns: a state saved before the grid registered
 * every column, or before a column existed, lists only some. Each registered column the state
 * doesn't list is slotted in after its nearest predecessor in `columns` (definition order) and
 * hidden, unless it is selected or `lockVisible`. Calendar and AG Grid's own columns are left to
 * the grid. A state without a column order, or one that lists every column, is returned unchanged.
 */
export const completeColumnState: SavedStateMigration = (state, columns, selectedColIds) => {
  const orderedColIds = state.columnOrder?.orderedColIds
  if (!orderedColIds) return state
  const listed = new Set(orderedColIds)
  const missing = columns.filter(
    ({ colId }) => !listed.has(colId) && !isCalendarColumn(colId) && !isAgGridInternalColumn(colId),
  )
  if (missing.length === 0) return state
  const missingIds = missing.map(({ colId }) => colId)
  const hidden = new Set(state.columnVisibility?.hiddenColIds)
  for (const col of missing) {
    if (!selectedColIds.has(col.colId) && !col.lockVisible) hidden.add(col.colId)
  }
  return {
    ...state,
    columnOrder: {
      orderedColIds: spliceAfterNearestPredecessor(
        columns.map(({ colId }) => colId),
        [...orderedColIds, ...missingIds],
        missingIds,
      ),
    },
    columnVisibility: { hiddenColIds: [...hidden] },
  }
}

/** The product table's saved-state migration: live calendar block, then every registered column. */
export const migrateProductTableState: SavedStateMigration = (state, columns, selectedColIds) =>
  completeColumnState(
    resolveCalendarBlockInState(state, columns, selectedColIds),
    columns,
    selectedColIds,
  )

function savedPinning(saved: GridState, colId: string): "left" | "right" | undefined {
  if (saved.columnPinning?.leftColIds.includes(colId)) return "left"
  if (saved.columnPinning?.rightColIds.includes(colId)) return "right"
  return undefined
}

/**
 * The saved sort for every live column, but only when the columns that were live all along still
 * sort as saved: a sort the user changed before the late columns arrived wins.
 */
function savedSortIfUntouched(
  saved: GridState,
  live: readonly ColumnState[],
  lateColIds: ReadonlySet<string>,
): ColumnState[] {
  const liveIds = new Set(live.map((col) => col.colId))
  const savedSort = (saved.sort?.sortModel ?? []).filter((item) => liveIds.has(item.colId))
  if (!savedSort.some((item) => lateColIds.has(item.colId))) return []

  const savedOthers = savedSort.filter((item) => !lateColIds.has(item.colId))
  const liveOthers = live
    .filter((col) => col.sort && !lateColIds.has(col.colId))
    .toSorted((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
  const untouched =
    savedOthers.length === liveOthers.length &&
    savedOthers.every(
      (item, i) => item.colId === liveOthers[i]!.colId && item.sort === liveOthers[i]!.sort,
    )
  if (!untouched) return []

  return savedSort.map((item, sortIndex) => ({
    colId: item.colId,
    sort: item.sort,
    sortIndex,
    sortType: item.type,
  }))
}

type LateColumns = { saved: GridState; colIds: readonly string[]; restoreWidths: boolean }

/**
 * Saved visibility, width, pinning, sort and aggregation of the late columns, where they differ
 * from live.
 */
function lateColumnPatches(
  { saved, colIds, restoreWidths }: LateColumns,
  live: readonly ColumnState[],
): Map<string, ColumnState> {
  const liveById = new Map(live.map((col) => [col.colId, col]))
  const late = new Set(colIds.filter((colId) => liveById.has(colId)))
  const patches = new Map<string, ColumnState>()
  const patch = (next: ColumnState) =>
    patches.set(next.colId, { ...patches.get(next.colId), ...next })

  const savedHidden = saved.columnVisibility && new Set(saved.columnVisibility.hiddenColIds)
  const savedWidths = new Map(savedWidthColumnState(saved).map((col) => [col.colId, col]))
  const savedAggFuncs = persistedAggFuncs(saved)
  for (const colId of late) {
    const current = liveById.get(colId)!
    const hide = savedHidden?.has(colId)
    if (hide !== undefined && !!current.hide !== hide) patch({ colId, hide })
    const pinned = savedPinning(saved, colId)
    if (pinned && current.pinned !== pinned) patch({ colId, pinned })
    const width = savedWidths.get(colId)
    if (restoreWidths && width && current.width !== width.width) patch(width)
    const aggFunc = savedAggFuncs.get(colId)
    if (aggFunc && current.aggFunc !== aggFunc) patch({ colId, aggFunc })
  }
  for (const sort of savedSortIfUntouched(saved, live, late)) {
    const current = liveById.get(sort.colId)!
    if (current.sort !== sort.sort || current.sortIndex !== sort.sortIndex) patch(sort)
  }
  return patches
}

/**
 * The one `applyColumnState({ state, applyOrder: true })` that places columns new to the grid: each
 * after its canonical neighbour, except `late` columns, which take their slot, visibility, width,
 * pinning, sort and aggregation from the saved state. New calendar columns go where `savedOrder`
 * had the calendar block, so a block replaced by another period keeps a position the user or a
 * view gave it. Every other column keeps its live position and state. `null` when the grid
 * already matches.
 */
export function placeNewColumns(
  live: readonly ColumnState[],
  canonicalIds: readonly string[],
  newColIds: readonly string[],
  { late, savedOrder }: { late?: LateColumns; savedOrder?: readonly string[] } = {},
): ColumnState[] | null {
  const liveOrder = live.map((col) => col.colId)
  const lateColIds = new Set(late?.colIds)
  const placing = newColIds.filter((colId) => !lateColIds.has(colId))
  const newCalendar = placing.filter(isCalendarColumn)
  const calendarReference =
    newCalendar.length > 0 && savedOrder?.some(isCalendarBlockEntry)
      ? resolveCalendarBlock(savedOrder, canonicalIds.filter(isCalendarColumn))
      : undefined
  const canonicalOrder = spliceAfterNearestPredecessor(
    canonicalIds,
    liveOrder,
    calendarReference ? placing.filter((colId) => !isCalendarColumn(colId)) : placing,
  )
  const calendarOrder = calendarReference
    ? spliceAfterNearestPredecessor(calendarReference, canonicalOrder, newCalendar)
    : canonicalOrder
  const order = late
    ? spliceAfterNearestPredecessor(
        late.saved.columnOrder?.orderedColIds ?? [],
        calendarOrder,
        late.colIds,
      )
    : calendarOrder
  const patches = late ? lateColumnPatches(late, live) : new Map<string, ColumnState>()

  const orderChanged = order.some((colId, i) => colId !== liveOrder[i])
  if (!orderChanged && patches.size === 0) return null
  return order.map((colId) => patches.get(colId) ?? { colId })
}

/**
 * Places the columns the grid `tableId` gained since the last call (see {@link placeNewColumns}).
 * A column accepted by `isLate` that missed the applied state takes its slot, visibility, pinning,
 * sort, aggregation and, with `restoreWidths`, width from that state. Returns whether any did.
 */
export function placeLoadedColumns(
  api: GridApi,
  tableId: string,
  { isLate, restoreWidths }: { isLate: (colId: string) => boolean; restoreWidths: boolean },
): boolean {
  const live = api.getColumnState()
  const { fresh, late } = takeNewColumns(
    tableId,
    live.map((col) => col.colId),
    isLate,
  )
  if (fresh.length === 0 && !late) return false
  const state = placeNewColumns(live, liveColIds(api), fresh, {
    late: late && { ...late, restoreWidths },
    savedOrder: fresh.some(isCalendarColumn) ? persistedColumnOrder(tableId) : undefined,
  })
  if (state) api.applyColumnState({ state, applyOrder: true })
  return !!late
}
